import {zodResolver} from "@hookform/resolvers/zod";
import {CloseOnboardingButton, ModalContentWrapper} from "@mytour/onboarding";
import {RamonagePdfReport, pdfService} from "@mytour/pdf";
import {
  downloadService,
  useContext,
  useCurrentSavedSignature,
  useWithdrawSavedSignature,
} from "@mytour/platform";
import {MAX_APPAREILS, MIN_APPAREILS, getCustomerFilenameSlug} from "@mytour/shared";
import type {
  Appareil,
  AppareilPrefill,
  CertificatRamonage,
  CertificatRamonagePayload,
  CertificatRamonagePrefill,
  ICompany,
  ICustomer,
  PlatformUserStyle,
  RamonagePhotoInput,
  SavedSignatureModel,
} from "@mytour/shared";
import {BackButton, MainButton, NextButton} from "@mytour/ui/form";
import {SplitterButtons} from "@mytour/ui/layout";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import axios from "axios";
import dayjs from "dayjs";
import {useEffect, useRef, useState} from "react";
import {useForm} from "react-hook-form";
import {toast} from "react-toastify";
import {Button} from "reactstrap";
import type {CertificateCustomerQueries} from "../types";
import {ApplianceProgressChip} from "./components/ApplianceProgressChip";
import {ReduceApplianceCountDialog} from "./components/ReduceApplianceCountDialog";
import type {ApplianceDetailsFormData} from "./fields/ApplianceDetailsForm";
import {
  ApplianceDetailsForm,
  applianceDetailsSchema,
  getApplianceDetailsDefaultValues,
} from "./fields/ApplianceDetailsForm";
import type {ApplianceFormData} from "./fields/ApplianceForm";
import {ApplianceForm, applianceSchema, getApplianceDefaultValues} from "./fields/ApplianceForm";
import type {CleaningFormData} from "./fields/CleaningForm";
import {CleaningForm, cleaningSchema, getCleaningDefaultValues} from "./fields/CleaningForm";
import type {InspectionFormData} from "./fields/InspectionForm";
import {
  InspectionForm,
  getInspectionDefaultValues,
  inspectionSchema,
} from "./fields/InspectionForm";
import type {InterventionFormData} from "./fields/InterventionForm";
import {
  InterventionForm,
  getInterventionDefaultValues,
  interventionSchema,
} from "./fields/InterventionForm";
import type {SignaturesFormData, TechnicianSignatureSource} from "./fields/SignaturesForm";
import {
  SignaturesForm,
  getSignaturesDefaultValues,
  signaturesSchema,
} from "./fields/SignaturesForm";
import {ApplianceCountStep} from "./steps/ApplianceCountStep";
import type {PhotoProcessingState, PhotosStepForm} from "./steps/AppliancePhotosStep";
import {AppliancePhotosStep, photosStepSchema} from "./steps/AppliancePhotosStep";
import type {SendCertificateEmailFormData} from "./steps/SendCertificateStep";
import {SendCertificateStep, sendCertificateEmailSchema} from "./steps/SendCertificateStep";

type LocalAppareil = Omit<Appareil, "photos"> & {photos: RamonagePhotoInput[]};

type DeviceSubStep = 0 | 1 | 2 | 3 | 4 | 5;

type WizardStep =
  | {kind: "count"}
  | {kind: "device"; deviceIndex: number; subStep: DeviceSubStep}
  | {kind: "signatures"}
  | {kind: "envoi"};

type Props = {
  customerQueries: CertificateCustomerQueries;
  customer: ICustomer;
  company: ICompany;
  platformUserStyle: PlatformUserStyle;
  defaultValues?: CertificatRamonagePrefill;
  intervenantName?: string;
  onSave: (data: CertificatRamonagePayload, sendEmail: boolean) => Promise<void>;
  onClose: () => void;
  onInteractionBlockedChange?: (blocked: boolean) => void;
};

const STEP_VALIDATION_ERROR_MESSAGE = "Veuillez corriger les champs signalés avant de continuer.";

export function CertificateWizard({
  customerQueries,
  customer,
  company,
  platformUserStyle,
  defaultValues,
  intervenantName,
  onSave,
  onClose,
  onInteractionBlockedChange,
}: Props) {
  const [pending, setIsPending] = useState(false);
  const pendingRef = useRef(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [photoState, setPhotoState] = useState<PhotoProcessingState>({
    isProcessing: false,
    hasError: false,
  });
  const photoStateRef = useRef(photoState);
  const fieldsDisabled = pending || photoState.isProcessing;
  const navigationDisabled = fieldsDisabled || photoState.hasError;
  const [sendEmail, setSendEmail] = useState(!!customer?.email);
  const [step, setStep] = useState<WizardStep>({kind: "count"});
  const {context} = useContext();
  const canEditCustomer =
    context?.role === "manager" ||
    (context?.role === "worker" && context.permissions.canEditCustomer);
  const queryClient = useQueryClient();
  const {
    savedSignature,
    isSavedSignatureLoading,
    isSavedSignatureFresh,
    savedSignatureError,
    refetchSavedSignature,
  } = useCurrentSavedSignature();
  const {withdrawSavedSignature} = useWithdrawSavedSignature();
  const [technicianSignatureSource, setTechnicianSignatureSource] =
    useState<TechnicianSignatureSource>("empty");
  const technicianSignatureSourceRef = useRef<TechnicianSignatureSource>("empty");
  const [hasStoredSignature, setHasStoredSignature] = useState(false);
  const [showConserveOption, setShowConserveOption] = useState(false);
  const initialSignatureReadRef = useRef(false);
  const signatureModelFingerprintRef = useRef<string | null>(null);
  const clientAttemptIdRef = useRef(crypto.randomUUID());
  const {mutateAsync: updateCustomer} = useMutation(customerQueries.update(queryClient));
  const formStep6 = useForm<SendCertificateEmailFormData>({
    resolver: zodResolver(sendCertificateEmailSchema),
    defaultValues: {email: ""},
  });
  const [reduceConfirm, setReduceConfirm] = useState<{fromCount: number; toCount: number} | null>(
    null
  );

  const initialCount = clampApplianceCount(defaultValues?.appareils?.length ?? 1);
  const [count, setCount] = useState(initialCount);
  const [appareils, setAppareils] = useState<LocalAppareil[]>(() =>
    Array.from({length: initialCount}, (_, i) => prefillAppliance(defaultValues?.appareils?.[i]))
  );

  const formStep0 = useForm<ApplianceFormData>({
    resolver: zodResolver(applianceSchema),
    defaultValues: getApplianceDefaultValues(appareils[0] ?? blankAppliance()),
  });
  const formStep1 = useForm<ApplianceDetailsFormData>({
    resolver: zodResolver(applianceDetailsSchema),
    defaultValues: getApplianceDetailsDefaultValues(appareils[0] ?? blankAppliance()),
  });
  const formStep2 = useForm<InterventionFormData>({
    resolver: zodResolver(interventionSchema),
    defaultValues: getInterventionDefaultValues(appareils[0] ?? blankAppliance()),
  });
  const formStep3 = useForm<CleaningFormData>({
    resolver: zodResolver(cleaningSchema),
    defaultValues: getCleaningDefaultValues(appareils[0] ?? blankAppliance()),
  });
  const formStep4 = useForm<InspectionFormData>({
    resolver: zodResolver(inspectionSchema),
    defaultValues: getInspectionDefaultValues(appareils[0] ?? blankAppliance()),
  });
  const formPhotos = useForm<PhotosStepForm>({
    resolver: zodResolver(photosStepSchema),
    defaultValues: {photos: appareils[0]?.photos ?? []},
  });
  const formStep5 = useForm<SignaturesFormData>({
    resolver: zodResolver(signaturesSchema),
    defaultValues: getSignaturesDefaultValues(),
  });

  useEffect(() => {
    if (
      !isSavedSignatureFresh ||
      savedSignatureError ||
      !savedSignature ||
      initialSignatureReadRef.current
    ) {
      return;
    }
    initialSignatureReadRef.current = true;
    signatureModelFingerprintRef.current = savedSignature.fingerprint;
    setHasStoredSignature(!!savedSignature.signature);
    if (savedSignature.signature && technicianSignatureSourceRef.current === "empty") {
      formStep5.setValue("signatureTechnicien", savedSignature.signature);
      formStep5.setValue("signatureTechnicienDate", new Date().toISOString());
      formStep5.setValue("conserveSignatureTechnicien", true);
      setTechnicianSignatureSource("applied");
      technicianSignatureSourceRef.current = "applied";
    } else if (savedSignature.signature && technicianSignatureSourceRef.current === "drawn") {
      formStep5.setValue("conserveSignatureTechnicien", true);
    }
  }, [isSavedSignatureFresh, savedSignatureError, savedSignature, formStep5.setValue]);

  const currentAppareilType = formStep0.watch("appareilType");
  const currentAppareilEmplacement = formStep0.watch("appareilEmplacement");
  const chipReminder =
    step.kind === "device" && step.subStep > 0 && currentAppareilType && currentAppareilEmplacement
      ? `${currentAppareilType} · ${currentAppareilEmplacement}`
      : undefined;

  return (
    <ModalContentWrapper>
      <div
        style={{
          overflowY: "auto",
          minHeight: "200px",
          maxHeight: "calc(100vh - 40px)",
          flexDirection: "column",
        }}
      >
        <h2 style={{marginTop: "8px", marginBottom: "16px"}} className="title-custom-style">
          Attestation de ramonage
        </h2>
        {step.kind === "device" && (
          <ApplianceProgressChip
            index={step.deviceIndex}
            count={count}
            reminder={chipReminder}
            showSingleAppareil={step.subStep === 5}
          />
        )}
        {step.kind === "count" && (
          <ApplianceCountStep
            count={count}
            onSelect={handleSelectCount}
            disabled={fieldsDisabled}
            prefillCount={defaultValues?.appareils?.length}
          />
        )}
        {step.kind === "device" && step.subStep === 0 && (
          <ApplianceForm
            control={formStep0.control}
            errors={formStep0.formState.errors}
            disabled={fieldsDisabled}
            watch={formStep0.watch}
            setValue={formStep0.setValue}
          />
        )}
        {step.kind === "device" && step.subStep === 1 && (
          <ApplianceDetailsForm
            control={formStep1.control}
            errors={formStep1.formState.errors}
            disabled={fieldsDisabled}
          />
        )}
        {step.kind === "device" && step.subStep === 2 && (
          <InterventionForm
            control={formStep2.control}
            errors={formStep2.formState.errors}
            disabled={fieldsDisabled}
          />
        )}
        {step.kind === "device" && step.subStep === 3 && (
          <CleaningForm
            control={formStep3.control}
            errors={formStep3.formState.errors}
            disabled={fieldsDisabled}
            watch={formStep3.watch}
            setValue={formStep3.setValue}
          />
        )}
        {step.kind === "device" && step.subStep === 4 && (
          <InspectionForm
            key={step.deviceIndex}
            control={formStep4.control}
            errors={formStep4.formState.errors}
            disabled={fieldsDisabled}
            trigger={formStep4.trigger}
          />
        )}
        {step.kind === "device" && step.subStep === 5 && (
          <AppliancePhotosStep
            key={step.deviceIndex}
            control={formPhotos.control}
            errors={formPhotos.formState.errors}
            disabled={fieldsDisabled}
            trigger={formPhotos.trigger}
            getValues={formPhotos.getValues}
            onStateChange={handlePhotoStateChange}
          />
        )}
        {step.kind === "signatures" && (
          <>
            {savedSignatureError && (
              <div role="alert" className="mb-2 text-danger">
                Impossible de vérifier votre signature conservée.{" "}
                <Button color="link" className="p-0" type="button" onClick={handleRetryModelRead}>
                  Réessayer
                </Button>
              </div>
            )}
            <SignaturesForm
              control={formStep5.control}
              errors={formStep5.formState.errors}
              disabled={
                fieldsDisabled ||
                isSavedSignatureLoading ||
                !isSavedSignatureFresh ||
                !!savedSignatureError
              }
              watch={formStep5.watch}
              getValues={formStep5.getValues}
              setValue={formStep5.setValue}
              technicianSignatureSource={technicianSignatureSource}
              hasStoredSignature={hasStoredSignature}
              showConserveOption={showConserveOption}
              onTechnicianDraw={handleTechnicianDraw}
              onTechnicianClear={handleTechnicianClear}
              onConserveChange={handleConserveChange}
            />
          </>
        )}
        {step.kind === "envoi" && (
          <SendCertificateStep
            customer={customer}
            data={collectAllData()}
            sendEmail={sendEmail}
            onToggleSendEmail={() => setSendEmail((prev) => !prev)}
            onDownloadPreview={handleDownloadPreview}
            disabled={fieldsDisabled}
            canEditCustomer={canEditCustomer}
            emailControl={formStep6.control}
            emailErrors={formStep6.formState.errors}
          />
        )}
      </div>
      {saveError && (
        <div role="alert" className="text-danger">
          {saveError}
        </div>
      )}
      {renderFooter()}
      {reduceConfirm && (
        <ReduceApplianceCountDialog
          fromCount={reduceConfirm.fromCount}
          toCount={reduceConfirm.toCount}
          removedLabel={describeRemovedAppliances(
            appareils,
            reduceConfirm.fromCount,
            reduceConfirm.toCount
          )}
          onCancel={handleCancelReduce}
          onConfirm={handleConfirmReduce}
        />
      )}
    </ModalContentWrapper>
  );

  function renderFooter() {
    if (step.kind === "count") {
      return (
        <SplitterButtons>
          <CloseOnboardingButton onClick={handleClose} disabled={navigationDisabled} />
          <NextButton
            onClick={handleToNextStep}
            disabled={navigationDisabled}
            pending={pending || photoState.isProcessing}
          />
        </SplitterButtons>
      );
    }
    if (step.kind === "envoi") {
      const willSendEmail =
        sendEmail || (!customer?.email && canEditCustomer && !!formStep6.watch("email").trim());
      return (
        <SplitterButtons>
          <BackButton onClick={handleToPrevStep} disabled={navigationDisabled} />
          <MainButton
            label={willSendEmail ? "Envoyer" : "Enregistrer"}
            onClick={handleFinalSubmit}
            disabled={navigationDisabled}
          />
        </SplitterButtons>
      );
    }
    return (
      <SplitterButtons>
        <BackButton onClick={handleToPrevStep} disabled={navigationDisabled} />
        <NextButton
          onClick={handleToNextStep}
          disabled={navigationDisabled}
          pending={pending || photoState.isProcessing}
        />
      </SplitterButtons>
    );
  }

  function setPending(value: boolean) {
    pendingRef.current = value;
    setIsPending(value);
    onInteractionBlockedChange?.(
      value || photoStateRef.current.isProcessing || photoStateRef.current.hasError
    );
  }

  function handlePhotoStateChange(state: PhotoProcessingState) {
    photoStateRef.current = state;
    onInteractionBlockedChange?.(pendingRef.current || state.isProcessing || state.hasError);
    setPhotoState(state);
  }

  function handleClose() {
    if (
      !pendingRef.current &&
      !photoStateRef.current.isProcessing &&
      !photoStateRef.current.hasError
    ) {
      onClose();
    }
  }

  function handleSelectCount(n: number) {
    if (
      pending ||
      photoStateRef.current.isProcessing ||
      photoStateRef.current.hasError ||
      n === count
    ) {
      return;
    }
    if (n < count) {
      setReduceConfirm({fromCount: count, toCount: n});
      return;
    }
    setAppareils((prev) => {
      const next = [...prev];
      for (let i = prev.length; i < n; i++) {
        next[i] = prefillAppliance(defaultValues?.appareils?.[i]);
      }
      return next;
    });
    setCount(n);
  }

  function handleConfirmReduce() {
    if (!reduceConfirm) {
      return;
    }
    setAppareils((prev) => prev.slice(0, reduceConfirm.toCount));
    setCount(reduceConfirm.toCount);
    setReduceConfirm(null);
  }

  function handleCancelReduce() {
    setReduceConfirm(null);
  }

  function getDeviceForm(subStep: DeviceSubStep) {
    switch (subStep) {
      case 0:
        return formStep0;
      case 1:
        return formStep1;
      case 2:
        return formStep2;
      case 3:
        return formStep3;
      case 4:
        return formStep4;
      case 5:
        return formPhotos;
    }
  }

  function loadAppareilIntoForms(appareil: LocalAppareil) {
    formStep0.reset(getApplianceDefaultValues(appareil));
    formStep1.reset(getApplianceDetailsDefaultValues(appareil));
    formStep2.reset(getInterventionDefaultValues(appareil));
    formStep3.reset(getCleaningDefaultValues(appareil));
    formStep4.reset(getInspectionDefaultValues(appareil));
    formPhotos.reset({photos: appareil.photos});
  }

  function readCurrentAppareilFromForms(): LocalAppareil {
    return {
      ...formStep0.getValues(),
      ...formStep1.getValues(),
      ...formStep2.getValues(),
      ...formStep3.getValues(),
      ...formStep4.getValues(),
      ...formPhotos.getValues(),
    };
  }

  async function handleToNextStep() {
    if (
      pendingRef.current ||
      photoStateRef.current.isProcessing ||
      photoStateRef.current.hasError
    ) {
      return;
    }
    if (step.kind === "count") {
      loadAppareilIntoForms(appareils[0] ?? blankAppliance());
      setStep({kind: "device", deviceIndex: 0, subStep: 0});
      return;
    }

    if (step.kind === "device") {
      setPending(true);
      try {
        const formStep = getDeviceForm(step.subStep);
        formStep.clearErrors();
        if (!(await formStep.trigger())) {
          toast.error(STEP_VALIDATION_ERROR_MESSAGE, {
            autoClose: 3000,
          });
          return;
        }

        if (step.subStep < 5) {
          setStep({...step, subStep: (step.subStep + 1) as DeviceSubStep});
          return;
        }

        if (photoStateRef.current.isProcessing || photoStateRef.current.hasError) {
          return;
        }
        const flushed = readCurrentAppareilFromForms();
        const nextAppareils = [...appareils];
        nextAppareils[step.deviceIndex] = flushed;
        setAppareils(nextAppareils);

        const nextDeviceIndex = step.deviceIndex + 1;
        if (nextDeviceIndex < count) {
          loadAppareilIntoForms(nextAppareils[nextDeviceIndex] ?? blankAppliance());
          setStep({kind: "device", deviceIndex: nextDeviceIndex, subStep: 0});
        } else {
          setStep({kind: "signatures"});
        }
      } finally {
        setPending(false);
      }
      return;
    }

    if (step.kind === "signatures") {
      if (!initialSignatureReadRef.current) {
        setSaveError("Impossible de vérifier votre signature conservée. Réessayez.");
        return;
      }
      setPending(true);
      try {
        formStep5.clearErrors();
        if (!(await formStep5.trigger())) {
          toast.error(STEP_VALIDATION_ERROR_MESSAGE, {
            autoClose: 3000,
          });
          return;
        }
        setStep({kind: "envoi"});
      } finally {
        setPending(false);
      }
    }
  }

  function handleToPrevStep() {
    if (
      pendingRef.current ||
      photoStateRef.current.isProcessing ||
      photoStateRef.current.hasError
    ) {
      return;
    }
    if (step.kind === "device") {
      if (step.subStep > 0) {
        setStep({...step, subStep: (step.subStep - 1) as DeviceSubStep});
        return;
      }
      const nextAppareils = [...appareils];
      nextAppareils[step.deviceIndex] = readCurrentAppareilFromForms();
      setAppareils(nextAppareils);

      if (step.deviceIndex === 0) {
        setStep({kind: "count"});
        return;
      }
      const prevDeviceIndex = step.deviceIndex - 1;
      loadAppareilIntoForms(nextAppareils[prevDeviceIndex] ?? blankAppliance());
      setStep({kind: "device", deviceIndex: prevDeviceIndex, subStep: 5});
      return;
    }
    if (step.kind === "signatures") {
      const lastDeviceIndex = count - 1;
      loadAppareilIntoForms(appareils[lastDeviceIndex] ?? blankAppliance());
      setStep({kind: "device", deviceIndex: lastDeviceIndex, subStep: 5});
      return;
    }
    if (step.kind === "envoi") {
      setStep({kind: "signatures"});
    }
  }

  async function handleFinalSubmit() {
    if (
      pendingRef.current ||
      photoStateRef.current.isProcessing ||
      photoStateRef.current.hasError
    ) {
      return;
    }
    setSaveError(null);
    setPending(true);
    try {
      let effectiveSendEmail = sendEmail;
      if (!customer?.email && canEditCustomer) {
        formStep6.clearErrors();
        if (!(await formStep6.trigger())) {
          return;
        }
        const email = formStep6.getValues("email").trim();
        if (email) {
          await updateCustomer({...customer, email});
          effectiveSendEmail = true;
        }
      }
      const data = collectAllData();
      await onSave(data, effectiveSendEmail);
    } catch (error) {
      if (isSignatureModelConflict(error)) {
        await handleModelConflict();
        return;
      }
      setSaveError(
        error instanceof Error
          ? error.message
          : "Erreur lors de la préparation de l'attestation. Veuillez réessayer."
      );
    } finally {
      setPending(false);
    }
  }

  function collectAllData(): CertificatRamonagePayload {
    const data5 = formStep5.getValues();
    return {
      date: new Date().toISOString(),
      appareils,
      ...data5,
      signatureModelFingerprint: signatureModelFingerprintRef.current,
      signatureTechnicienSource: technicianSignatureSource === "applied" ? "applied" : "drawn",
      clientAttemptId: clientAttemptIdRef.current,
    };
  }

  function handleTechnicianDraw() {
    technicianSignatureSourceRef.current = "drawn";
    setTechnicianSignatureSource("drawn");
    setShowConserveOption(true);
  }

  function handleTechnicianClear() {
    technicianSignatureSourceRef.current = "empty";
    setTechnicianSignatureSource("empty");
    setShowConserveOption(hasStoredSignature);
    if (hasStoredSignature) {
      formStep5.setValue("conserveSignatureTechnicien", true);
    }
  }

  async function handleConserveChange(conserve: boolean) {
    if (conserve) {
      formStep5.setValue("conserveSignatureTechnicien", true);
      return;
    }
    if (hasStoredSignature) {
      const model = await withdrawSavedSignature();
      signatureModelFingerprintRef.current = model.fingerprint;
      setHasStoredSignature(false);
    }
    formStep5.setValue("conserveSignatureTechnicien", false);
  }

  async function handleRetryModelRead() {
    const result = await refetchSavedSignature();
    if (result.isSuccess && result.data && !initialSignatureReadRef.current) {
      applyCurrentModel(result.data);
    }
  }

  async function handleModelConflict() {
    if (technicianSignatureSourceRef.current === "applied") {
      formStep5.setValue("signatureTechnicien", "");
      formStep5.setValue("signatureTechnicienDate", "");
      technicianSignatureSourceRef.current = "empty";
      setTechnicianSignatureSource("empty");
    }
    formStep5.setValue("conserveSignatureTechnicien", false);
    setShowConserveOption(technicianSignatureSourceRef.current === "drawn");
    setStep({kind: "signatures"});
    const result = await refetchSavedSignature();
    if (!result.isSuccess || !result.data) {
      setSaveError("Impossible de vérifier la signature conservée. Réessayez.");
      return;
    }
    applyCurrentModel(result.data, false);
    setSaveError(
      "La signature conservée a changé. Vérifiez votre tracé puis choisissez à nouveau « Conserver » si souhaité."
    );
  }

  function applyCurrentModel(model: SavedSignatureModel, prefill = true) {
    initialSignatureReadRef.current = true;
    signatureModelFingerprintRef.current = model.fingerprint;
    setHasStoredSignature(!!model.signature);
    if (prefill && model.signature && technicianSignatureSourceRef.current === "empty") {
      formStep5.setValue("signatureTechnicien", model.signature);
      formStep5.setValue("signatureTechnicienDate", new Date().toISOString());
      formStep5.setValue("conserveSignatureTechnicien", true);
      technicianSignatureSourceRef.current = "applied";
      setTechnicianSignatureSource("applied");
    }
  }

  async function createPdfBlob(data: CertificatRamonage) {
    const pdfStyle = pdfService.getStyle(platformUserStyle);
    return await pdfService.toBlob(
      <RamonagePdfReport
        data={data}
        company={company}
        customer={customer}
        intervenantName={intervenantName}
        pdfStyle={pdfStyle}
      />
    );
  }

  async function handleDownloadPreview() {
    const data = collectAllData();
    const blob = await createPdfBlob({...data, certificateNumber: "(en cours)"});
    const parsedDate = dayjs().format("YYYY-MM-DD");
    const fileName = `attestation-ramonage_${getCustomerFilenameSlug(customer)}_${parsedDate}.pdf`;
    await downloadService.download(blob, fileName);
  }
}

function isSignatureModelConflict(error: unknown): boolean {
  return (
    axios.isAxiosError(error) &&
    error.response?.status === 409 &&
    error.response.data?.code === "SIGNATURE_MODEL_CHANGED"
  );
}

function clampApplianceCount(n: number): number {
  return Math.min(MAX_APPAREILS, Math.max(MIN_APPAREILS, n));
}

function blankAppliance(): LocalAppareil {
  return {
    appareilType: "",
    appareilTypeCustom: "",
    appareilEmplacement: "",
    appareilMarque: "",
    appareilModele: "",
    appareilPuissance: "",
    appareilNumeroSerie: "",
    appareilDateInstallation: "",
    appareilGarantie: "",
    combustible: "",
    conduitType: "",
    arriveeAir: "",
    natureIntervention: [],
    controleVisuelAppareil: "oui",
    controleVisuelConduit: "oui",
    controleVisuelSouche: "oui",
    nettoyageAction: "",
    nettoyageOutil: "",
    nettoyageMethodeAcces: [],
    tirage: "conforme",
    etatConduit: "",
    typeSuies: "",
    volumeSuies: "",
    aspirationSuies: true,
    remarques: "",
    photos: [],
    attestationVacuite: false,
  };
}

/**
 * Préremplit un appareil depuis la dernière attestation du client — installation
 * uniquement (type, emplacement, conduit, marque/modèle…). Le constat de CETTE
 * intervention (nature, nettoyage, contrôles et état, remarques, vacuité) démarre
 * toujours vide : reconduire le constat d'une visite passée sur un document légal
 * risquerait de faire passer un ancien défaut pour un état actuel non réévalué.
 */
function prefillAppliance(source: AppareilPrefill | undefined): LocalAppareil {
  if (!source) {
    return blankAppliance();
  }
  return {
    ...blankAppliance(),
    appareilType: source.appareilType,
    appareilTypeCustom: source.appareilTypeCustom,
    appareilEmplacement: source.appareilEmplacement,
    appareilMarque: source.appareilMarque,
    appareilModele: source.appareilModele,
    appareilPuissance: source.appareilPuissance,
    appareilNumeroSerie: source.appareilNumeroSerie,
    appareilDateInstallation: source.appareilDateInstallation,
    appareilGarantie: source.appareilGarantie,
    combustible: source.combustible,
    conduitType: source.conduitType,
    arriveeAir: source.arriveeAir,
  };
}

function describeRemovedAppliances(
  appareils: Appareil[],
  fromCount: number,
  toCount: number
): string {
  if (fromCount - toCount === 1) {
    const removed = appareils[toCount];
    const details =
      removed?.appareilType && removed.appareilEmplacement
        ? ` (${removed.appareilType}, ${removed.appareilEmplacement})`
        : "";
    return `l'appareil ${fromCount}${details}`;
  }
  return `les appareils ${toCount + 1} à ${fromCount}`;
}
